import { History } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';
import { extractApiError } from '~/utils/extract-api-error';
import { formatBytes } from '~/utils/formatting';

import { useEstimateExport, useTriggerExport } from '../../hooks/use-export-mutations';
import type {
  ExportConnector,
  ExportFilters,
  ExportFormat,
  ExportFormatOption,
  ExportTriggerRequest,
} from '../../types/export.types';
import { ExportHistorySheet } from '../export-history-sheet';

import { ExportFormatRadios } from './export-format-radios';

/** Settling time before a filter or column change re-estimates — the drawer's
 *  value, so the two surfaces feel the same. */
const ESTIMATE_DEBOUNCE_MS = 400;

interface BrowseExportBarProps {
  sourceWorkflowId: string;
  /** Human context for the progress toast and the history sheet. */
  label: string;
  connector: ExportConnector;
  /** The export request minus `format` — `fields`, `filters` and `template`
   *  exactly as the table above is showing them. */
  request: Omit<ExportTriggerRequest, 'format'>;
  formats: readonly ExportFormatOption[];
  format: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  /** True when a feed template is applied, which is when the delimiter
   *  recommendation on a format means something. */
  feedTemplateApplied: boolean;
  /** Search or header-row filters are active: the table is narrower than the file. */
  viewFiltersActive: boolean;
  /** Reasons Start export must be blocked, already decided by the caller. */
  identifierOnly: boolean;
  templateUnsupported: boolean;
}

/**
 * The page's export footer: what the file will contain, how it is delimited,
 * and the button that starts it.
 *
 * Sits at the bottom because exporting is what you do AFTER narrowing, and
 * reads left to right as one sentence — "about 1,284 products (~4.1 MB) · CSV ·
 * Start export" — so the number the operator is about to act on is the first
 * thing in the row.
 *
 * The estimate is driven by the same `fields` + `filters` the table uses, so it
 * agrees with the total badge in the toolbar whenever no view-only filter is
 * active. When one IS active the bar says so in words: the trigger endpoint has
 * no `search` or `columnFilters`, so a search that narrows the table to 40
 * products still exports every product the export filters match, and nothing
 * else on the page makes that visible.
 */
export function BrowseExportBar({
  sourceWorkflowId,
  label,
  connector,
  request,
  formats,
  format,
  onFormatChange,
  feedTemplateApplied,
  viewFiltersActive,
  identifierOnly,
  templateUnsupported,
}: BrowseExportBarProps) {
  const { t } = useTranslation('exports');
  const estimate = useEstimateExport();
  const trigger = useTriggerExport();
  const [historyOpen, setHistoryOpen] = useState(false);

  // Re-estimate when the request changes, debounced. `request` is memoised by
  // the caller on its inputs, so identity IS change here; the cleanup clears
  // the pending timer, so a burst of filter ticks lands as one request and an
  // unmount mid-debounce fires nothing. A response that arrives after a newer
  // `mutate` is dropped by `useMutation`, which only observes its latest call.
  const { mutate: runEstimate } = estimate;
  useEffect(() => {
    const timer = window.setTimeout(() => {
      runEstimate(request);
    }, ESTIMATE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [request, runEstimate]);

  const estimateData = estimate.data;
  const isEmpty = estimateData?.expectedProducts === 0;

  const confirmDisabled =
    estimate.isPending || estimate.isError || isEmpty || identifierOnly || templateUnsupported;

  // A same-tick second click still sees `trigger.isPending` false (the mutation observer
  // notifies on the next macrotask) — the ref latch is what stops a double-click export.
  const startingRef = useRef(false);
  const handleConfirm = () => {
    // `aria-disabled`, not `disabled`, so the button can still host the tooltip
    // explaining why — which means the guard lives here.
    if (confirmDisabled || trigger.isPending || startingRef.current) return;
    startingRef.current = true;
    trigger.mutate(
      { request: { ...request, format }, label, connector },
      {
        onSettled: () => {
          startingRef.current = false;
        },
      },
    );
  };

  // The filters the file will carry, spelled out so `filters` in the request
  // type stays the single source of truth for what is sent.
  const filters: ExportFilters | undefined = request.filters;

  return (
    <>
      <div className="bg-muted/30 flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md border px-4 py-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="text-foreground flex flex-wrap items-center gap-x-2 text-sm font-medium">
            {estimate.isPending && <Skeleton className="h-5 w-56" />}
            {estimate.isError && (
              <span className="text-error text-sm font-normal">
                {extractApiError(estimate.error, t('estimate.error'))}
              </span>
            )}
            {estimateData &&
              (isEmpty
                ? t('estimate.empty')
                : t('estimate.summary', {
                    count: estimateData.expectedProducts,
                    size: formatBytes(estimateData.expectedFileSizeBytes),
                  }))}
            {filters?.successOnly === false && (
              <Badge variant="outline" className="pointer-events-none text-[11px] font-normal">
                {t('browse.exportBar.includesFailed')}
              </Badge>
            )}
          </div>
          {/* The one thing this page can get wrong silently, said out loud. */}
          {viewFiltersActive && (
            <p className="text-warning text-xs">{t('browse.exportBar.viewFiltersNote')}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <ExportFormatRadios
            formats={formats}
            format={format}
            onFormatChange={onFormatChange}
            feedTemplateApplied={feedTemplateApplied}
          />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground h-9 cursor-pointer text-xs"
            onClick={() => {
              setHistoryOpen(true);
            }}
          >
            <History aria-hidden="true" className="mr-1.5 size-4" />
            {t('history.title')}
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                onClick={handleConfirm}
                isLoading={trigger.isPending}
                aria-disabled={confirmDisabled}
                className={cn(
                  confirmDisabled
                    ? 'pointer-events-auto cursor-not-allowed opacity-50'
                    : 'cursor-pointer',
                )}
              >
                {t('estimate.confirm')}
              </Button>
            </TooltipTrigger>
            {(templateUnsupported || identifierOnly) && (
              <TooltipContent className="max-w-xs">
                {templateUnsupported
                  ? t('estimate.template.triggerUnsupported')
                  : t('estimate.columns.noneSelected')}
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>

      <ExportHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        sourceWorkflowId={sourceWorkflowId}
      />
    </>
  );
}
